import {
  ASCENSION_POWER_LEVEL_CAP,
  ASCENSION_POWER_STEP,
  roundAscensionMultiplier,
} from "./ascension_balance.js";
import {
  applyAscensionGoldCurve,
  sanctuaryGoldFactor,
  scaleReward,
  towerBossGoldFactor,
} from "./economy_modifiers.mjs";

/** Or par boss d’épreuve (zones 1–18) et capstone (zone 19) — aligné combat. */
export const BATTLE_TOWER_TRIAL_BOSS_GOLD_BASE = 50_000_000;
/** XP dresseur par boss d’épreuve / capstone. */
export const BATTLE_TOWER_TRIAL_BOSS_XP_BASE = 2_000_000;

/** Coût invocation Paldea (référence gacha — aligné SCALE 0.2). */
export const PALDEA_BANNER_COST_GOLD = 540_000;
export const BATTLE_TOWER_SANCTUARY_COMBATS_PER_X50 = 10;

/**
 * Sync `COMBAT_GOLD_REWARD_SCALE` / `BATTLE_TOWER_SANCTUARY_GOLD_BASE`
 * (`shared/combat_balance.ts`).
 */
const COMBAT_GOLD_REWARD_SCALE = 0.2;
const BATTLE_TOWER_SANCTUARY_GOLD_BASE = 50_000;

const ASCENSION_REWARD_GOLD_STEP = 0.05;

export function getAscensionRewardMult(
  level: number,
  goldBoostRanks: number,
): number {
  const effectivePowerLevel = Math.min(
    ASCENSION_POWER_LEVEL_CAP,
    Math.max(0, Math.floor(level)),
  );
  return roundAscensionMultiplier(
    1 +
      effectivePowerLevel * ASCENSION_POWER_STEP +
      Math.max(0, Math.floor(goldBoostRanks)) * ASCENSION_REWARD_GOLD_STEP,
  );
}

export function getPaldeaInvocationGoldBatch50(): number {
  return PALDEA_BANNER_COST_GOLD * 50;
}

/**
 * Or par sauvage zone farm tour — base fixe × rewardMult (pas le coût bannière).
 * `sanctuaryGoldFactor()` = modificateur admin live : c'est le levier qui permet
 * de nerfer le sanctuaire sans toucher aux routes (il ne passe pas par
 * `COMBAT_GOLD_REWARD_SCALE`, contrairement aux sauvages et aux boss d'épreuve).
 */
export function getBattleTowerSanctuaryGoldPerKill(rewardMult = 1): number {
  return Math.max(
    1,
    scaleReward(
      BATTLE_TOWER_SANCTUARY_GOLD_BASE * applyAscensionGoldCurve(rewardMult),
      sanctuaryGoldFactor(),
    ),
  );
}

export function getBattleTowerTrialBossGold(rewardMult = 1): number {
  return Math.max(
    1,
    scaleReward(
      BATTLE_TOWER_TRIAL_BOSS_GOLD_BASE *
        applyAscensionGoldCurve(rewardMult) *
        COMBAT_GOLD_REWARD_SCALE,
      towerBossGoldFactor(),
    ),
  );
}

export function getBattleTowerTrialBossXp(rewardMult = 1): number {
  return Math.max(1, Math.round(BATTLE_TOWER_TRIAL_BOSS_XP_BASE * rewardMult));
}

export type BattleTowerGoldCreditKind = "trial" | "capstone" | "farm";

export function battleTowerGoldCreditKind(
  zone: number | null | undefined,
): BattleTowerGoldCreditKind | null {
  const z = zone == null ? null : Math.floor(Number(zone));
  if (z == null || !Number.isFinite(z)) return null;
  if (z >= 1 && z <= 18) return "trial";
  if (z === 19) return "capstone";
  if (z === 20) return "farm";
  return null;
}

/** Budget kills/sync farm sanctuaire — early game. */
export const SANCTUARY_FARM_KILL_BUDGET_MIN = 50;
/** Budget kills/sync farm sanctuaire — ascension endgame. */
export const SANCTUARY_FARM_KILL_BUDGET_MAX = 200;

/**
 * Kills max crédités par requête farm sanctuaire — monte avec l’ascension et la fenêtre sync.
 */
export function resolveSanctuaryFarmKillBudget(
  rewardMult: number,
  elapsedSec: number,
): number {
  const mult = Math.max(0.001, rewardMult);
  const elapsed = Math.max(1, Math.min(120, Math.floor(elapsedSec)));
  const fromAsc = SANCTUARY_FARM_KILL_BUDGET_MIN + Math.floor((mult - 1) * 15);
  const fromTime = Math.floor(elapsed * 0.8);
  return Math.min(
    SANCTUARY_FARM_KILL_BUDGET_MAX,
    Math.max(SANCTUARY_FARM_KILL_BUDGET_MIN, fromAsc, fromTime),
  );
}

/** Débit or/h estimé côté serveur au farm sanctuaire (HUD). */
export function estimateSanctuaryFarmCreditPerHour(
  rewardMult: number,
  opts?: { elapsedSec?: number; syncIntervalSec?: number; maxPasses?: number },
): number {
  const elapsedSec = opts?.elapsedSec ?? 30;
  const syncIntervalSec = Math.max(1, opts?.syncIntervalSec ?? 30);
  const maxPasses = Math.max(1, opts?.maxPasses ?? 5);
  const perSync = maxBattleTowerCombatGoldCredit({
    kind: "farm",
    rewardMult,
    requested: Number.MAX_SAFE_INTEGER,
    elapsedSec,
  });
  return Math.floor((perSync * maxPasses * 3600) / syncIntervalSec);
}

/** Plafond de crédit or pour une sync combat en mode tour (hors estimation routes). */
export function maxBattleTowerCombatGoldCredit(params: {
  kind: BattleTowerGoldCreditKind;
  rewardMult: number;
  requested: number;
  elapsedSec: number;
}): number {
  const requested = Math.max(0, Math.floor(params.requested));
  if (requested <= 0) return 0;

  const mult = Math.max(0.001, params.rewardMult);
  const elapsed = Math.max(1, Math.min(120, Math.floor(params.elapsedSec)));

  if (params.kind === "farm") {
    const perKill = getBattleTowerSanctuaryGoldPerKill(mult);
    const killBudget = resolveSanctuaryFarmKillBudget(mult, elapsed);
    const byKills = Math.min(
      killBudget,
      Math.max(1, Math.ceil(requested / perKill)),
    );
    const maxKillsPerSec = mult >= 10 ? 5 : 2;
    const timeBudget = Math.floor(elapsed * maxKillsPerSec + 8);
    return Math.min(requested, perKill * Math.min(byKills, timeBudget));
  }

  const perBoss = getBattleTowerTrialBossGold(mult);
  const maxBosses = Math.min(2, Math.max(1, Math.ceil(requested / perBoss)));
  return Math.min(requested, perBoss * maxBosses);
}
